import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import { coerceLocale, muscleName, type ModelExtra } from "@heraion/shared";

import { Button } from "../primitives/Button";
import { joinList } from "../joinList";
import { Eyebrow } from "../primitives/Eyebrow";
import { SimulationMuscleRow } from "../Programs/SimulationMuscleRow";
import { BodyweightOnlyToggle } from "../Progress/BodyweightOnlyToggle";
import { makeSimulationStyles } from "../Programs/simulationStyles";
import type { UiTheme } from "../theme";
import type { GapRow, ModelWizardGapsState } from "./useModelWizardGaps";

// #492 F3, under the days: each muscle under MEV with one exercise, added in
// one tap to the day with the most time left, or a line saying there is no
// room. The muscles with none share one line. Over MRV: said only.
export function ModelWizardGaps({
  theme,
  gaps,
  nameOf,
  onAdd,
}: {
  theme: UiTheme;
  gaps: ModelWizardGapsState;
  nameOf: (slug: string) => string;
  onAdd: (extra: ModelExtra) => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = coerceLocale(i18n.language);
  const sim = makeSimulationStyles(theme);
  const styles = makeStyles(theme);
  const { below, above, bodyweightOnly, setBodyweightOnly, noBodyweight, dayName, added, say } = gaps;
  if (!gaps.ready || (below.length === 0 && above.length === 0)) return null;
  // Nothing to suggest, one line for all; the switch's note names those the filter left bare.
  const bare = below.filter((r) => r.offer === null && !r.noBodyweight).map((r) => muscleName(r.muscle.muscle, lang));

  const offerLine = (row: GapRow) => {
    const muscle = muscleName(row.muscle.muscle, lang);
    const { offer } = row;
    if (offer === null) return null;
    const exercise = nameOf(offer.slug);
    const extra = offer.extra;
    if (extra === null) {
      return (
        <Text style={sim.note}>
          {t("programModel.wizard.gaps.noDay", { muscle, defaultValue: "No training day can take an exercise: {{muscle}} stays under MEV." })}
        </Text>
      );
    }
    const day = dayName(extra.dayId);
    if (!offer.fits) {
      return (
        <Text style={sim.note}>
          {t("programModel.wizard.gaps.noRoom", {
            day,
            exercise,
            muscle,
            defaultValue:
              "No room: {{day}} has the most time left, and {{exercise}} does not fit. {{muscle}} stays under MEV: raise the minutes or take out another exercise.",
          })}
        </Text>
      );
    }
    return (
      <View style={styles.offer}>
        <Text style={sim.note}>
          {t("programModel.wizard.gaps.suggested", { exercise, defaultValue: "Suggested: {{exercise}}" })}
        </Text>
        <Button
          theme={theme}
          variant="secondary"
          compact
          icon="plus"
          label={t("programModel.wizard.gaps.addTo", { day, defaultValue: "Add to {{day}}" })}
          accessibilityLabel={t("programModel.wizard.gaps.addA11y", {
            exercise,
            day,
            muscle,
            defaultValue: "Add {{exercise}} to {{day}}, for {{muscle}}",
          })}
          onPress={() => {
            onAdd(extra);
            say(t("programModel.wizard.gaps.added", { exercise, day, defaultValue: "{{exercise}} added to {{day}}." }));
          }}
        />
      </View>
    );
  };

  return (
    <View style={styles.block}>
      <Text style={styles.title} accessibilityRole="header">
        {t("programModel.wizard.gaps.title", { defaultValue: "Hard sets a week, against the band" })}
      </Text>
      {below.length > 0 ? (
        <>
          <BodyweightOnlyToggle
            theme={theme}
            value={bodyweightOnly}
            onValueChange={setBodyweightOnly}
            noBodyweight={noBodyweight}
            sublabel={t("programModel.wizard.gaps.bodyweightOnlyHint", {
              defaultValue: "The suggestions use only bodyweight exercises. Not saved.",
            })}
          />
          <Eyebrow theme={theme}>
            {`${t("programModel.wizard.gaps.belowTitle", { defaultValue: "Under MEV" })} · ${t("simulator.direction.unlikelyToGrow", { defaultValue: "likely too little to grow" })}`}
          </Eyebrow>
          {below.map((row, i) => (
            <View key={row.muscle.muscle} style={styles.item}>
              <SimulationMuscleRow
                variant="gap"
                theme={theme}
                styles={sim}
                muscle={row.muscle}
                days={row.days}
                allWeeks={row.allWeeks}
                lang={lang}
                divider={i > 0}
              />
              {offerLine(row)}
            </View>
          ))}
          {bare.length > 0 ? (
            <Text style={sim.note}>
              {t("programModel.wizard.gaps.noSuggestion", {
                muscles: joinList(bare, i18n.language),
                defaultValue: "No suggestion: {{muscles}}.",
              })}
            </Text>
          ) : null}
        </>
      ) : null}
      {above.length > 0 ? (
        <>
          <Eyebrow theme={theme}>{t("programModel.wizard.gaps.aboveTitle", { defaultValue: "Over MRV" })}</Eyebrow>
          {above.map((row, i) => (
            <SimulationMuscleRow key={row.muscle.muscle} theme={theme} styles={sim} muscle={row.muscle} days={row.days} lang={lang} divider={i > 0} />
          ))}
          <Text style={sim.note}>
            {t("programModel.wizard.gaps.aboveNote", { defaultValue: "Flagged only: nothing is cut to bring it down." })}
          </Text>
        </>
      ) : null}
      <View accessibilityLiveRegion="polite">{added ? <Text style={sim.quiet}>{added}</Text> : null}</View>
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    block: { gap: s.s3 },
    title: { color: p.ink, fontSize: ty.sizes.base, fontWeight: "700" },
    item: { gap: s.s2 },
    // The button always on its own line under the suggestion, at any width.
    offer: { gap: s.s2, alignItems: "flex-start" },
  });
}
